import React, { useState, useEffect } from 'react';
import { X, Smartphone, Laptop, Share, PlusSquare, Check, HelpCircle, Download, Monitor, ShieldCheck, Sparkles, RefreshCw } from 'lucide-react';

interface PwaHandlerProps {
  isStandalone: boolean;
  canInstall: boolean; // Native install prompt support (deferredPrompt != null)
  onInstall: () => void; // Trigger native install
  forceShowGuide?: boolean;
  onCloseGuide?: () => void;
  settings?: any;
}

const PwaHandler: React.FC<PwaHandlerProps> = ({ 
  isStandalone, 
  canInstall, 
  onInstall,
  forceShowGuide,
  onCloseGuide,
  settings
}) => {
  const [activeTab, setActiveTab] = useState<'ios' | 'android' | 'desktop'>('android');
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [showManualSafari, setShowManualSafari] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const [effectiveCanInstall, setEffectiveCanInstall] = useState(canInstall || !!(window as any).deferredPrompt);

  useEffect(() => {
    const checkPrompt = () => {
      const promptAvailable = canInstall || !!(window as any).deferredPrompt;
      setEffectiveCanInstall(promptAvailable);
    };

    checkPrompt();
    const interval = setInterval(checkPrompt, 300);
    window.addEventListener('pwa-prompt-available', checkPrompt);
    window.addEventListener('beforeinstallprompt', checkPrompt);

    return () => {
      clearInterval(interval);
      window.removeEventListener('pwa-prompt-available', checkPrompt);
      window.removeEventListener('beforeinstallprompt', checkPrompt);
    };
  }, [canInstall]);

  useEffect(() => {
    const checkPlatformAndUa = () => {
      const ua = navigator.userAgent.toLowerCase();
      const isMobileUA = /iphone|ipad|ipod|android|blackberry|mini|windows\sphone|palm/i.test(ua);
      const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
      const mobileStatus = isMobileUA || (hasTouch && window.innerWidth < 1024);
      
      setIsMobileDevice(mobileStatus);

      if (/iphone|ipad|ipod/.test(ua)) {
        setPlatform('ios');
        setActiveTab('ios');
      } else if (/android/.test(ua)) {
        setPlatform('android');
        setActiveTab('android');
      } else {
        setPlatform('desktop');
        setActiveTab('desktop');
      }
    };
    
    checkPlatformAndUa();
  }, []);

  const handleNativeInstallClick = () => {
    onInstall();
  };

  const handleClose = () => {
    if (onCloseGuide) {
      onCloseGuide();
    }
  };

  const handleOpenInNewTab = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('action', 'install_pwa');
    window.open(url.toString(), '_blank');
  };

  const handleRetryInstall = () => {
    setRetrying(true);
    
    // Check if prompt is globally ready
    const activePrompt = (window as any).deferredPrompt;
    if (activePrompt) {
      onInstall();
      setTimeout(() => setRetrying(false), 1000);
      return;
    }

    // If inside an iframe, synchronously open in new tab to bypass iframe restriction
    if (window.self !== window.top) {
      handleOpenInNewTab();
      setRetrying(false);
      return;
    }

    // Call onInstall which triggers SW update check
    onInstall();
    setTimeout(() => setRetrying(false), 1500);
  };

  if (isStandalone) return null;

  const appIcon = settings?.pwaIconUrl || settings?.logoUrl || '/pwa-192x192.png';
  const appName = settings?.pwaName || settings?.siteTitle || 'Ecossistema Digital';
  const appDescription = settings?.pwaDescription || settings?.siteDescription || 'Seu ecossistema digital inteligente - Cardápio, Bio Links, Portfólio e Blog';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md select-none transition-all duration-300">
      <div 
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0b0922] border border-white/10 rounded-[2rem] shadow-[0_30px_100px_rgba(0,0,0,0.9)] w-full max-w-md overflow-hidden flex flex-col text-white transform scale-100 transition-all duration-300"
      >
        {/* Top Colored Accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-5 right-5 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white transition-all cursor-pointer z-10"
        >
          <X size={16} />
        </button>

        {/* --- CASE 1: DIRECT NATIVE PWA INSTALLATION IS AVAILABLE --- */}
        {effectiveCanInstall ? (
          <div className="p-6 md:p-8 flex flex-col items-center text-center space-y-6">
            {/* Pulsating Glowing Icon Container */}
            <div className="relative mt-4">
              <div className="absolute inset-0 rounded-3xl bg-indigo-500/20 blur-xl animate-pulse" />
              <img 
                src={appIcon} 
                alt={appName} 
                className="relative w-20 h-20 md:w-24 md:h-24 rounded-3xl object-contain border border-white/10 shadow-2xl" 
                referrerPolicy="no-referrer"
              />
              <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-slate-950 p-1 rounded-full border-2 border-[#0b0922] shadow-lg">
                <ShieldCheck size={16} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="inline-flex items-center gap-1 bg-indigo-500/10 border border-indigo-400/20 px-3 py-1 rounded-full">
                <Sparkles size={11} className="text-indigo-400" />
                <span className="text-[9px] font-black uppercase tracking-wider text-indigo-300">Aplicativo Oficial Verificado</span>
              </div>
              <h2 className="text-xl md:text-2xl font-black text-white tracking-tight">{appName}</h2>
              <p className="text-xs text-slate-400 font-medium leading-relaxed max-w-xs mx-auto">
                {appDescription}
              </p>
            </div>

            {/* Premium Perks Grid */}
            <div className="w-full bg-white/5 border border-white/5 p-4 rounded-2xl space-y-2.5 text-left">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <Check size={12} />
                </div>
                <span className="text-[11px] font-bold text-slate-200">Acesso super rápido diretamente da sua tela</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <Check size={12} />
                </div>
                <span className="text-[11px] font-bold text-slate-200">Experiência imersiva em tela cheia (Sem abas)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <Check size={12} />
                </div>
                <span className="text-[11px] font-bold text-slate-200">Otimizado para economia de dados e bateria</span>
              </div>
            </div>

            {/* Glowing CTA Button */}
            <button
              onClick={handleNativeInstallClick}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-slate-950 font-black text-xs uppercase tracking-wider py-4 px-6 rounded-2xl cursor-pointer transition-all shadow-[0_10px_30px_rgba(16,185,129,0.3)] hover:shadow-[0_15px_35px_rgba(16,185,129,0.4)] active:scale-95 text-center flex items-center justify-center gap-2 hover:scale-[1.02] duration-200"
            >
              <Download size={15} /> INSTALAR AGORA NATIVAMENTE
            </button>

            <button 
              onClick={handleClose}
              className="text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-all cursor-pointer"
            >
              Voltar para o site
            </button>
          </div>
        ) : (
          /* --- CASE 2: NATIVE SYSTEM PROMPT IS NOT YET READY / FALLBACKS --- */
          <div className="flex flex-col">
            {/* Header Area */}
            <div className="px-6 pt-7 pb-5 border-b border-white/5 bg-gradient-to-b from-indigo-950/20 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-600/10 border border-indigo-500/20 rounded-xl flex items-center justify-center text-indigo-400 shrink-0">
                  <Download size={20} className="animate-bounce" />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase tracking-wider text-[#ffb900]">Instalar Aplicativo</h3>
                  <p className="text-[10px] text-slate-400 font-medium leading-none mt-1">Siga a instalação rápida abaixo</p>
                </div>
              </div>
            </div>

            {/* Dynamic Multi-Tab Selector */}
            <div className="px-6 pt-4">
              <div className="bg-black/45 p-1 rounded-xl border border-white/5 grid grid-cols-3 gap-1">
                <button
                  onClick={() => setActiveTab('ios')}
                  className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'ios' 
                      ? 'bg-indigo-600 text-white shadow-lg' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Smartphone size={11} /> IPHONE
                </button>
                <button
                  onClick={() => setActiveTab('android')}
                  className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'android' 
                      ? 'bg-indigo-600 text-white shadow-lg' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Smartphone size={11} /> ANDROID
                </button>
                <button
                  onClick={() => setActiveTab('desktop')}
                  className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all cursor-pointer ${
                    activeTab === 'desktop' 
                      ? 'bg-indigo-600 text-white shadow-lg' 
                      : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Monitor size={11} /> DESKTOP
                </button>
              </div>
            </div>

            {/* Tab Specific Content */}
            <div className="p-6 space-y-4 flex-1">
              {/* IPHONE / IOS TAB - WITH PREMIUM CONFIG PROFILE AUTOPROMPT */}
              {activeTab === 'ios' && (
                <div className="space-y-4 animate-fade-in text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Smartphone size={16} className="text-indigo-400 shrink-0" />
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-100">INSTALADOR AUTOMÁTICO DE PERFIL (MUITO MAIS FÁCIL)</h4>
                  </div>
                  <p className="text-[10px] text-slate-300 leading-relaxed font-medium">
                    Instale o aplicativo oficial de forma imediata e limpa, ignorando o tutorial tradicional do Safari:
                  </p>

                  {/* Profile installation button */}
                  <div className="bg-gradient-to-br from-indigo-950/40 via-slate-900/40 to-[#0b0922] border border-indigo-500/30 p-4 rounded-2xl space-y-3.5 shadow-lg relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-24 h-24 bg-indigo-500/10 rounded-full blur-xl" />
                    
                    <div className="flex items-center gap-2">
                      <span className="text-[#ffb900] text-sm">✨</span>
                      <p className="text-[10px] font-black uppercase tracking-wider text-indigo-300">Instalador Automático Homologado</p>
                    </div>
                    
                    <a
                      href="/pwa-install.mobileconfig"
                      className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#ffb900] to-[#e0a300] hover:from-[#f0b000] hover:to-[#d09900] text-slate-950 font-black text-xs uppercase tracking-wider py-3.5 px-4 rounded-xl cursor-pointer transition-all active:scale-95 text-center shadow-lg hover:scale-[1.01]"
                    >
                      <Download size={14} /> INSTALAR APLICATIVO NATIVO
                    </a>

                    {/* Step list for Profile */}
                    <div className="space-y-2 text-[10px] text-slate-300 bg-black/20 p-3 rounded-xl border border-white/5">
                      <div className="flex gap-2">
                        <span className="font-extrabold text-[#ffb900]">1.</span>
                        <span>Clique no botão acima para baixar o instalador oficial.</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-extrabold text-[#ffb900]">2.</span>
                        <span>Abra os <strong>Ajustes do seu iPhone</strong> e clique em <strong>"Perfil Baixado"</strong> bem no topo.</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="font-extrabold text-[#ffb900]">3.</span>
                        <span>Clique em <strong>"Instalar"</strong> no canto superior direito e pronto!</span>
                      </div>
                    </div>
                  </div>

                  {/* Manual Safari Accordion Toggle */}
                  <div className="border-t border-white/5 pt-3">
                    <button
                      onClick={() => setShowManualSafari(!showManualSafari)}
                      className="w-full flex items-center justify-between text-[10px] font-bold text-indigo-400 hover:text-indigo-300 transition-all uppercase tracking-wider py-1 cursor-pointer"
                    >
                      <span>Alternativa: Instalar pelo Safari manualmente</span>
                      <span className="text-xs">{showManualSafari ? '▲' : '▼'}</span>
                    </button>

                    {showManualSafari && (
                      <div className="space-y-3 mt-3 animate-fade-in bg-white/5 p-4 rounded-xl border border-white/5">
                        <p className="text-[10px] text-slate-300 font-medium">Instalação clássica pelo navegador Safari:</p>
                        <div className="space-y-2">
                          <div className="flex gap-3 items-start">
                            <div className="w-5 h-5 rounded-lg bg-white/5 border border-white/10 text-white font-black text-[10px] flex items-center justify-center shrink-0">1</div>
                            <p className="text-[10px] text-slate-300 leading-normal">
                              Toque no botão de <strong>Compartilhar <Share size={10} className="inline text-indigo-400 mx-0.5" /></strong> na barra inferior do Safari.
                            </p>
                          </div>
                          <div className="flex gap-3 items-start">
                            <div className="w-5 h-5 rounded-lg bg-white/5 border border-white/10 text-white font-black text-[10px] flex items-center justify-center shrink-0">2</div>
                            <p className="text-[10px] text-slate-300 leading-normal">
                              Role para baixo e toque em <strong>"Adicionar à Tela de Início <PlusSquare size={10} className="inline text-indigo-400 mx-0.5" />"</strong>.
                            </p>
                          </div>
                          <div className="flex gap-3 items-start">
                            <div className="w-5 h-5 rounded-lg bg-white/5 border border-white/10 text-white font-black text-[10px] flex items-center justify-center shrink-0">3</div>
                            <p className="text-[10px] text-slate-300 leading-normal">
                              Toque em <strong>"Adicionar"</strong> no canto superior direito para confirmar.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ANDROID TAB - WITH AUTOMATED TRIGGER / RETRY */}
              {activeTab === 'android' && (
                <div className="space-y-4 animate-fade-in text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Smartphone size={16} className="text-indigo-400 shrink-0" />
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-100">INSTALAR NO ANDROID</h4>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/5 space-y-3">
                    <p className="text-[10px] text-slate-300 leading-relaxed font-medium">
                      O instalador do Android é acionado automaticamente pelo navegador Google Chrome.
                    </p>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleRetryInstall}
                        disabled={retrying}
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-black text-xs uppercase tracking-wider py-3.5 px-4 rounded-xl cursor-pointer transition-all active:scale-95"
                      >
                        {retrying ? (
                          <>
                            <RefreshCw size={13} className="animate-spin" /> ACIONANDO INSTALADOR...
                          </>
                        ) : (
                          <>
                            <Download size={13} /> ACIONAR INSTALAÇÃO DIRETA
                          </>
                        )}
                      </button>

                      <button
                        onClick={handleOpenInNewTab}
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white font-bold text-[10px] uppercase tracking-wider py-2.5 px-3 rounded-xl cursor-pointer transition-all border border-white/10"
                      >
                        <Monitor size={12} /> ABRIR EM TELA CHEIA PARA INSTALAR
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">Se a instalação direta não abrir:</p>
                    {/* Step 1 */}
                    <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-xl border border-white/5">
                      <div className="w-5 h-5 rounded bg-[#ffb900] text-slate-950 font-black text-[10px] flex items-center justify-center shrink-0">1</div>
                      <div className="text-[10px]">
                        <p className="font-bold text-white mb-0.5">Menu do Chrome</p>
                        <p className="text-slate-300 leading-normal">Toque nos três pontos no canto superior direito do seu Chrome.</p>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-xl border border-white/5">
                      <div className="w-5 h-5 rounded bg-[#ffb900] text-slate-950 font-black text-[10px] flex items-center justify-center shrink-0">2</div>
                      <div className="text-[10px]">
                        <p className="font-bold text-white mb-0.5">Instalar aplicativo</p>
                        <p className="text-slate-300 leading-normal">Selecione <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong>.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* DESKTOP / COMPUTADOR TAB - WITH AUTO RETRY */}
              {activeTab === 'desktop' && (
                <div className="space-y-4 animate-fade-in text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <Laptop size={16} className="text-indigo-400 shrink-0" />
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-100">INSTALAR NO COMPUTADOR / MAC</h4>
                  </div>
                  
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/5 space-y-3">
                    <p className="text-[10px] text-slate-300 leading-relaxed font-medium">
                      Funciona perfeitamente no Chrome, Edge ou Brave, criando um aplicativo dedicado no seu Dock/Barra de tarefas.
                    </p>

                    <div className="flex flex-col gap-2">
                      <button
                        onClick={handleRetryInstall}
                        disabled={retrying}
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-600/50 text-white font-black text-xs uppercase tracking-wider py-3.5 px-4 rounded-xl cursor-pointer transition-all active:scale-95"
                      >
                        {retrying ? (
                          <>
                            <RefreshCw size={13} className="animate-spin" /> ACIONANDO INSTALADOR...
                          </>
                        ) : (
                          <>
                            <Download size={13} /> ENVIAR COMANDO DE INSTALAÇÃO
                          </>
                        )}
                      </button>

                      <button
                        onClick={handleOpenInNewTab}
                        className="w-full inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white font-bold text-[10px] uppercase tracking-wider py-2.5 px-3 rounded-xl cursor-pointer transition-all border border-white/10"
                      >
                        <Monitor size={12} /> ABRIR EM TELA CHEIA PARA INSTALAR
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">Se a instalação direta não abrir:</p>
                    {/* Step 1 */}
                    <div className="flex gap-3 items-start bg-white/5 p-3.5 rounded-xl border border-white/5">
                      <div className="w-5 h-5 rounded bg-[#ffb900] text-slate-950 font-black text-[10px] flex items-center justify-center shrink-0">1</div>
                      <div className="text-[10px]">
                        <p className="font-bold text-white mb-0.5">Barra de Endereços (+)</p>
                        <p className="text-slate-300 leading-normal">
                          Clique no ícone de <strong>"+"</strong> ou no monitor com seta no canto direito superior da barra de endereços do navegador Chrome/Edge.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Close Area */}
            <div className="bg-[#08061a] p-5 border-t border-white/5 flex justify-end">
              <button 
                onClick={handleClose}
                className="w-full sm:w-auto bg-white/5 hover:bg-white/10 text-white border border-white/10 font-bold text-[10px] uppercase tracking-wider py-3.5 px-6 rounded-xl cursor-pointer transition-all active:scale-95 text-center"
              >
                FECHAR E VOLTAR
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PwaHandler;
